// src/pages/ThankYou.js
// Post-checkout thank-you page (PRD-53)
// Reads session_id from URL, fetches subscription details, displays plan + dates + CTAs

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import SEOHead from '../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../components/common';
import { clearTierCache } from '../services/tierService';
import './ThankYou.css';

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

function formatDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatPrice(amount, currency, interval) {
  if (amount == null) return '';
  const formatted = amount.toLocaleString('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
  return `${formatted}${interval === 'year' ? '/year' : '/month'}`;
}

function ThankYou() {
  const [searchParams] = useSearchParams();
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading'); // loading | success | error | no-session
  const [details, setDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setStatus('no-session');
      return;
    }

    if (!isAuthenticated || !accounts[0]) {
      // User must be authenticated to retrieve session details — but if they're not,
      // we still show a generic success state.
      setStatus('no-session');
      return;
    }

    let cancelled = false;

    async function fetchDetails() {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });
        const idToken = tokenResponse.idToken;

        const url = `${API_BASE_URL}/stripe-checkout-status?session_id=${encodeURIComponent(sessionId)}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (cancelled) return;

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          setErrorMessage(`Could not retrieve checkout details (${response.status}). ${text}`);
          setStatus('error');
          return;
        }

        const data = await response.json();
        setDetails(data);
        setStatus('success');
      } catch (err) {
        if (!cancelled) {
          console.error('ThankYou fetch error:', err);
          setErrorMessage(err.message || 'Failed to retrieve checkout details.');
          setStatus('error');
        }
      }
    }

    fetchDetails();
    return () => { cancelled = true; };
  }, [sessionId, isAuthenticated, accounts, instance]);

  // Drop the 60s tierCache so the Navbar pill + downstream feature gates
  // reflect the new tier immediately instead of waiting for TTL expiry.
  useEffect(() => {
    if (status !== 'success' || !details) return;
    clearTierCache();
  }, [status, details]);

  return (
    <div className="thankyou-container">
      <SEOHead
        title="Thank You — DriftAlarm"
        description="Your DriftAlarm subscription is active."
        path="/thank-you"
      />

      <PublicHeader variant="minimal" />

      <div className="thankyou-content">
        {status === 'loading' && (
          <div className="thankyou-card thankyou-card--loading">
            <div className="thankyou-spinner" />
            <h2>Confirming your subscription…</h2>
            <p>This usually takes a couple of seconds.</p>
          </div>
        )}

        {status === 'no-session' && (
          <div className="thankyou-card thankyou-card--success">
            <div className="thankyou-success-icon">&#10003;</div>
            <h1 className="thankyou-title">Thanks for subscribing to DriftAlarm</h1>
            <p className="thankyou-subtitle">
              Your subscription is being activated. You can head to your dashboard now.
            </p>
            <div className="thankyou-cta-row">
              <Link to="/command-center" className="da-btn da-btn--primary">
                Go to Command Center
              </Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="thankyou-card thankyou-card--error">
            <h2>We couldn't load your subscription details</h2>
            <p className="thankyou-error-message">{errorMessage}</p>
            <p>
              Don't worry — your payment was processed by Stripe. Your subscription will be active
              within a minute or two. Head to your dashboard or contact{' '}
              <a href="mailto:support@driftalarm.com">support@driftalarm.com</a> if anything looks wrong.
            </p>
            <div className="thankyou-cta-row">
              <Link to="/command-center" className="da-btn da-btn--primary">
                Go to Command Center
              </Link>
            </div>
          </div>
        )}

        {status === 'success' && details && (
          <div className="thankyou-card thankyou-card--success">
            <div className="thankyou-success-icon">&#10003;</div>
            <h1 className="thankyou-title">
              Welcome to DriftAlarm {details.tierDisplayName || ''}
            </h1>
            <p className="thankyou-subtitle">
              Your subscription is active. Continuous attack-surface monitoring is now running.
            </p>

            <div className="thankyou-details">
              <div className="thankyou-detail-row">
                <span className="thankyou-detail-label">Plan</span>
                <span className="thankyou-detail-value">
                  DriftAlarm {details.tierDisplayName || details.tier}
                </span>
              </div>
              <div className="thankyou-detail-row">
                <span className="thankyou-detail-label">Billing</span>
                <span className="thankyou-detail-value">
                  {details.billingInterval === 'year' ? 'Annual' : 'Monthly'}
                </span>
              </div>
              {details.amount != null && (
                <div className="thankyou-detail-row">
                  <span className="thankyou-detail-label">Price</span>
                  <span className="thankyou-detail-value">
                    {formatPrice(details.amount, details.currency, details.billingInterval)}
                  </span>
                </div>
              )}
              {details.periodStart && (
                <div className="thankyou-detail-row">
                  <span className="thankyou-detail-label">Start date</span>
                  <span className="thankyou-detail-value">{formatDate(details.periodStart)}</span>
                </div>
              )}
              {details.periodEnd && (
                <div className="thankyou-detail-row">
                  <span className="thankyou-detail-label">Next billing date</span>
                  <span className="thankyou-detail-value">{formatDate(details.periodEnd)}</span>
                </div>
              )}
              {details.customerEmail && (
                <div className="thankyou-detail-row">
                  <span className="thankyou-detail-label">Receipt sent to</span>
                  <span className="thankyou-detail-value">{details.customerEmail}</span>
                </div>
              )}
            </div>

            <div className="thankyou-cta-grid">
              <Link to="/fast-scan" className="thankyou-cta-card">
                <div className="thankyou-cta-title">Scan Something</div>
                <div className="thankyou-cta-desc">Run a fast scan against any domain or IP.</div>
              </Link>
              <Link to="/command-center" className="thankyou-cta-card thankyou-cta-card--primary">
                <div className="thankyou-cta-title">Go to Command Center</div>
                <div className="thankyou-cta-desc">See your full security posture at a glance.</div>
              </Link>
              <Link to="/assets" className="thankyou-cta-card">
                <div className="thankyou-cta-title">Add an Asset</div>
                <div className="thankyou-cta-desc">Start continuous monitoring on a new asset.</div>
              </Link>
            </div>

            <p className="thankyou-portal-link">
              Need to update your payment method or cancel?{' '}
              <Link to="/settings">Manage your subscription</Link>.
            </p>
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}

export default ThankYou;
